import React, { memo, useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import CampaignDetailsHeader from './CampaignDetailsHeader';
import CampaignDetailsFormContent from './CampaignDetailsFormContent';
import HtmlEditorOverlay from './HtmlEditorOverlay';
import SenderFormModal from '../SenderFormModal';
import './styles.scss';

const MOCK_CAMPAIGN = {
    id: 1,
    title: '',
    status: getLiteral('label_draft'),
    sender: {
        name: 'Lisa Matthew',
        email: 'lisa_matthew@cglobal.com',
    },
};

const STATUS_BADGE_TYPES = {
    [getLiteral('label_draft')]: 'default',
};

const MOCK_SENDERS = [
    {
        label: 'lisa_matthew@cglobal.com',
        value: '1',
        subLabel: 'Lisa Matthew',
    },
    {
        label: 'marketing@sage.com',
        value: '2',
        subLabel: 'Marketing Team',
    },
    {
        label: 'sales@company.com',
        value: '3',
        subLabel: 'Sales Department',
    },
];

const MOCK_RECIPIENTS_USAGE = {
    selectedRecipients: 0,
    maxRecipients: 20000,
    remainingEmails: 20000,
};

const CampaignDetails = memo(
    ({
        campaign = MOCK_CAMPAIGN,
        recipientsUsage: initialRecipientsUsage = MOCK_RECIPIENTS_USAGE,
        onBack,
        onPreview,
        onSend,
        onAddRecipients,
        onCancelRecipients,
        onSaveRecipients,
    }) => {
        const theme = useTheme();
        const [campaignTitle, setCampaignTitle] = useState(campaign.title || '');
        const [campaignHtml, setCampaignHtml] = useState(campaign.html);
        const [expandedSections, setExpandedSections] = useState({
            sender: false,
            recipients: false,
            subject: false,
            design: false,
            additionalSettings: false,
        });
        const [isHtmlEditorOpen, setIsHtmlEditorOpen] = useState(false);

        // Local state for recipients usage (fake implementation)
        const [recipientsUsage, setRecipientsUsage] = useState(initialRecipientsUsage);

        // Sender modal state
        const [isSenderModalOpen, setIsSenderModalOpen] = useState(false);
        const [senders, setSenders] = useState(MOCK_SENDERS);
        const [isCreatingSender, setIsCreatingSender] = useState(false);

        // Lifted state from SenderForm for controlled selection
        const [selectedSenderId, setSelectedSenderId] = useState(campaign.sender?.value || '');
        const [senderName, setSenderName] = useState(campaign.sender?.name || '');
        const [subject, setSubject] = useState('');
        const [previewText, setPreviewText] = useState('');

        useEffect(() => {
            setCampaignTitle(campaign.title || '');
            setCampaignHtml(campaign.html);
        }, [campaign]);

        const currentCampaign = useMemo(
            () => ({
                ...campaign,
                title: campaignTitle,
                html: campaignHtml,
            }),
            [campaign, campaignHtml, campaignTitle],
        );

        const hasExpandedSection = useMemo(() => {
            return Object.values(expandedSections).some(Boolean);
        }, [expandedSections]);

        const completedSections = useMemo(
            () => ({
                sender: true,
                recipients: false,
                subject: subject.trim().length > 0,
                design: campaignHtml?.trim?.().length > 0,
                additionalSettings: false,
            }),
            [campaignHtml, subject],
        );

        const handleToggleSection = useCallback((section) => {
            setExpandedSections((prev) => ({
                ...prev,
                [section]: !prev[section],
            }));
        }, []);

        const handleBack = useCallback(() => {
            onBack?.();
        }, [onBack]);

        const handlePreview = useCallback(() => {
            onPreview?.(currentCampaign);
        }, [currentCampaign, onPreview]);

        const handleSend = useCallback(() => {
            onSend?.(currentCampaign);
        }, [currentCampaign, onSend]);

        const handleAddRecipients = useCallback(() => {
            setRecipientsUsage((prev) => {
                const newSelected = prev.selectedRecipients + 1200;
                const newRemaining = Math.max(0, prev.remainingEmails - 1200);
                return {
                    ...prev,
                    selectedRecipients: newSelected,
                    remainingEmails: newRemaining,
                };
            });
            onAddRecipients?.(campaign);
        }, [onAddRecipients, campaign]);

        const handleCancelRecipients = useCallback(() => {
            onCancelRecipients?.(campaign);
        }, [onCancelRecipients, campaign]);

        const handleSaveRecipients = useCallback(() => {
            onSaveRecipients?.(campaign);
        }, [onSaveRecipients, campaign]);

        const handleSubjectChange = useCallback((value) => {
            setSubject(value);
        }, []);

        const handlePreviewTextChange = useCallback((value) => {
            setPreviewText(value);
        }, []);

        const handleTitleSave = useCallback((newTitle) => {
            setCampaignTitle(newTitle);
        }, []);

        const handleDesignOptionSelect = useCallback((option) => {
            if (option?.title === 'HTML Editor') {
                setIsHtmlEditorOpen(true);
            }
        }, []);

        const handleHtmlEditorExit = useCallback((discardChanges = true) => {
            if (discardChanges) {
                console.log('Discarding HTML editor changes');
            }
            setIsHtmlEditorOpen(false);
        }, []);

        const handleHtmlEditorSave = useCallback(
            async ({ html, title }) => {
                console.log('Persist marketing campaign HTML', {
                    campaignId: campaign.id,
                    title,
                    html,
                });

                setCampaignTitle(title);
                setCampaignHtml(html);
            },
            [campaign.id],
        );

        const handleSaveSender = useCallback(
            ({ senderId, name }) => {
                console.log('Saving sender:', { senderId, name });
                // TODO: Update campaign sender data
                handleToggleSection('sender');
            },
            [handleToggleSection],
        );

        // Sender modal handlers
        const handleOpenSenderModal = useCallback(() => {
            setIsSenderModalOpen(true);
        }, []);

        const handleCloseSenderModal = useCallback(() => {
            setIsSenderModalOpen(false);
        }, []);

        const handleCreateSender = useCallback(
            async (formData) => {
                setIsCreatingSender(true);
                try {
                    // Mock service call - replace with actual API call when available
                    // const newSender = await senderService.create(formData);

                    // Simulate API delay
                    await new Promise((resolve) => setTimeout(resolve, 500));

                    // Create new sender object matching the format
                    const newSender = {
                        label: formData.fromEmail,
                        value: Date.now().toString(),
                        subLabel: formData.fromName,
                    };

                    // Update senders list
                    setSenders((prev) => [...prev, newSender]);

                    // Automatically select the new sender
                    setSelectedSenderId(newSender.value);
                    setSenderName(formData.fromName);

                    handleCloseSenderModal();
                } catch (error) {
                    console.error('Failed to create sender:', error);
                    // TODO: Show error notification
                } finally {
                    setIsCreatingSender(false);
                }
            },
            [handleCloseSenderModal],
        );

        // Handler for when sender selection changes in SenderForm
        const handleSenderSelectionChange = useCallback((senderId) => {
            setSelectedSenderId(senderId);
        }, []);

        // Handler for when sender name changes in SenderForm
        const handleSenderNameChange = useCallback((name) => {
            setSenderName(name);
        }, []);

        const badgeType = STATUS_BADGE_TYPES[campaign.status] || 'default';
        const shouldShowHtmlEditor = isHtmlEditorOpen;
        const containerClassName = [
            'campaign-details__container',
            hasExpandedSection ? 'campaign-details__container--expanded' : '',
            shouldShowHtmlEditor ? 'campaign-details__container--html-editor' : '',
        ].join(' ');
        const contentClassName = [
            'campaign-details__content',
            shouldShowHtmlEditor ? 'campaign-details__content--covered' : '',
        ].join(' ');
        const formContent = (
            <CampaignDetailsFormContent
                campaign={campaign}
                completedSections={completedSections}
                expandedSections={expandedSections}
                hasExpandedSection={hasExpandedSection}
                recipientsUsage={recipientsUsage}
                senderName={senderName}
                senders={senders}
                selectedSenderId={selectedSenderId}
                subject={subject}
                previewText={previewText}
                theme={theme}
                onAddRecipients={handleAddRecipients}
                onCancelRecipients={handleCancelRecipients}
                onDesignOptionSelect={handleDesignOptionSelect}
                onOpenSenderModal={handleOpenSenderModal}
                onPreviewTextChange={handlePreviewTextChange}
                onSaveRecipients={handleSaveRecipients}
                onSaveSender={handleSaveSender}
                onSelectedSenderChange={handleSenderSelectionChange}
                onSenderNameChange={handleSenderNameChange}
                onSubjectChange={handleSubjectChange}
                onToggleSection={handleToggleSection}
            />
        );

        return (
            <div className="campaign-details">
                <div
                    className={[
                        'campaign-details__main',
                        shouldShowHtmlEditor ? 'campaign-details__main--covered' : '',
                    ].join(' ')}
                >
                    <CampaignDetailsHeader
                        badgeType={badgeType}
                        campaignTitle={campaignTitle}
                        onBack={handleBack}
                        onPreview={handlePreview}
                        onSend={handleSend}
                        onTitleChange={setCampaignTitle}
                        onTitleSave={handleTitleSave}
                        status={campaign.status}
                    />

                    <div className={containerClassName}>
                        <div className={contentClassName}>{formContent}</div>
                    </div>
                </div>

                <div
                    className={[
                        'campaign-details__editor-overlay',
                        shouldShowHtmlEditor ? 'campaign-details__editor-overlay--open' : '',
                    ].join(' ')}
                    aria-hidden={!shouldShowHtmlEditor}
                >
                    <HtmlEditorOverlay
                        campaignId={campaign.id}
                        initialHtml={campaignHtml}
                        initialTitle={campaignTitle}
                        isOpen={shouldShowHtmlEditor}
                        onSave={handleHtmlEditorSave}
                        onExitWithoutSaving={handleHtmlEditorExit}
                    />
                </div>

                <SenderFormModal
                    isOpen={isSenderModalOpen}
                    onCancel={handleCloseSenderModal}
                    onCreate={handleCreateSender}
                    onRequestClose={handleCloseSenderModal}
                />
            </div>
        );
    },
);

CampaignDetails.displayName = 'CampaignDetails';

CampaignDetails.propTypes = {
    campaign: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        status: PropTypes.oneOf(['Draft', 'Scheduled', 'Sent']),
        sender: PropTypes.shape({
            name: PropTypes.string,
            email: PropTypes.string,
        }),
    }),
    recipientsUsage: PropTypes.shape({
        selectedRecipients: PropTypes.number,
        maxRecipients: PropTypes.number,
        remainingEmails: PropTypes.number,
    }),
    onBack: PropTypes.func,
    onPreview: PropTypes.func,
    onSend: PropTypes.func,
    onAddRecipients: PropTypes.func,
    onCancelRecipients: PropTypes.func,
    onSaveRecipients: PropTypes.func,
};

export default CampaignDetails;
